import React, { useMemo } from 'react';

import { is3pProductKey, type ProductKeys, type ProductKeys3P } from '../../../constants/products';
import {
	type ConnectorSource,
	FULL_CONNECTOR_SOURCE,
	SMARTLINKS_CONNECTOR_SOURCE,
	type ThirdPartyConfigsBootstrap,
} from '../../../constants/schemas/3p-config';
import { isProductSmartlink } from '../../connectors';
import type { OAuthSource } from '../../events/oauth-events';
import { usePostOfficePlacement } from '../use-post-office-placement';

interface OAuthHandlersProps {
	product: ProductKeys;
	connectorSource: ConnectorSource;
	successFlagTitle: React.ReactNode;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	isOnSearchDialog?: boolean;
	extraAttributes?: Record<string, unknown>;
}

interface OauthHandlerFunctions {
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
}

export type useOAuthHandlers = (props: OAuthHandlersProps) => OauthHandlerFunctions;

interface ConnectorForPlacement {
	connectorId: string;
	connectorIndex: number;
	connectorMode: ConnectorSource;
	connectorProductKey: ProductKeys;
	connectorName: string;
	icon: NonNullable<ThirdPartyConfigsBootstrap[string]>['icon'];
	oAuthUrl: string;
	userNeedsOAuth: boolean;
}

// Shape expected by search-page-results-banner placement AppProps, in post-office/integrated-teams/rovo-expansion/placement-v2/src/search-page-results-*/index.tsx
type BannerPlacementPropsPayload = {
	availableConnectors: ConnectorForPlacement[];
	useOAuthHandlers: useOAuthHandlers;
	onMessageViewed?: (trackImpression: () => void) => void;
};

// Build config object with only keys in availableProductsOrder, in that order.
export const orderConfigByProductOrder = (
	configs: ThirdPartyConfigsBootstrap,
	availableProductsOrder: string[],
): ThirdPartyConfigsBootstrap => {
	const ordered = {} as ThirdPartyConfigsBootstrap;
	for (const productKey of availableProductsOrder) {
		const config = configs[productKey as ProductKeys3P];
		if (config) {
			ordered[productKey as ProductKeys3P] = config;
		}
	}
	return ordered;
};

// Map bootstrap 3P configs to the placement's ConnectorProps shape
export const mapToAvailableConnectors = (
	configs: ThirdPartyConfigsBootstrap,
): ConnectorForPlacement[] => {
	const entries = Object.entries(configs).filter(
		(entry): entry is [string, NonNullable<ThirdPartyConfigsBootstrap[string]>] => {
			const [productKey, config] = entry;
			return is3pProductKey(productKey) && config != null && config.outboundAuthUrl != null;
		},
	);
	return entries.map(([productKey, config], index) => {
		const isSmartlink = isProductSmartlink({
			productKey,
			thirdPartyConfigs: configs,
		});
		const connectorName = typeof config.displayName === 'string' ? config.displayName : '';
		return {
			connectorId: config.id,
			connectorIndex: index,
			connectorMode: isSmartlink ? SMARTLINKS_CONNECTOR_SOURCE : FULL_CONNECTOR_SOURCE,
			connectorName,
			connectorProductKey: productKey as ProductKeys,
			icon: config.icon,
			oAuthUrl: config.outboundAuthUrl,
			userNeedsOAuth: config.userNeedsOAuth ?? false,
		};
	});
};

interface BannerPlacementProps {
	placementId: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProductsOrder: string[]; // Product keys in display order; connectors will be shown in this order
	useOAuthHandlers: useOAuthHandlers;
	onMessageViewed?: (trackImpression: () => void) => void;
}

export const BannerPlacement = ({
	placementId,
	thirdPartyConfigs,
	availableProductsOrder,
	useOAuthHandlers,
	onMessageViewed,
}: BannerPlacementProps): React.JSX.Element | null => {
	const { PostOfficePlacement, isMounted } = usePostOfficePlacement();

	const orderedConfigs = useMemo(
		() => orderConfigByProductOrder(thirdPartyConfigs, availableProductsOrder),
		[thirdPartyConfigs, availableProductsOrder],
	);

	const availableConnectors = useMemo(
		() => mapToAvailableConnectors(orderedConfigs),
		[orderedConfigs],
	);

	// the props passed into the placement
	const placementProps: BannerPlacementPropsPayload = {
		availableConnectors,
		useOAuthHandlers,
		onMessageViewed,
	};

	if (!isMounted || !PostOfficePlacement) {
		return null;
	}

	return <PostOfficePlacement placementId={placementId} placementProps={placementProps} />;
};
